import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../config/axios';

const PROVIDERS = [
  { value: 'shopify', label: 'Shopify' },
  { value: 'ikas', label: 'ikas' },
  { value: 'ticimax', label: 'Ticimax' }
];

function buildWebhookUrl(integrationId, provider) {
  const base = window.location.origin;
  return `${base}/api/integrations/${integrationId}/webhook/${provider}`;
}

export default function IntegrationsPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [firms, setFirms] = useState([]);
  const [integrations, setIntegrations] = useState([]);

  const [filterFirm, setFilterFirm] = useState('');
  const [filterProvider, setFilterProvider] = useState('');

  const [createForm, setCreateForm] = useState({
    firm_slug: '',
    provider: 'shopify',
    status: 'active',
    webhook_secret: '',
    enqueue_to_message_pool: false,
    default_country: 'TR'
  });

  const [selectedIntegration, setSelectedIntegration] = useState(null);

  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editErr, setEditErr] = useState('');
  const [editOk, setEditOk] = useState('');
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [eventsLimit, setEventsLimit] = useState(50);
  const [eventsOffset, setEventsOffset] = useState(0);

  const [eventsFilter, setEventsFilter] = useState({
    status: '',
    event_type: '',
    provider_event_id: '',
    raw_body_sha256: '',
    payload_sha256: ''
  });

  const [eventActions, setEventActions] = useState([]);
  const [eventActionsErr, setEventActionsErr] = useState('');
  const [eventActionsLoading, setEventActionsLoading] = useState(false);

  const [eventActionLoading, setEventActionLoading] = useState(false);
  const [eventNote, setEventNote] = useState('');

  const [testSending, setTestSending] = useState(false);
  const [testForm, setTestForm] = useState({
    event_type: '',
    provider_event_id: '',
    payload_json: '{\n  "test": true\n}',
    headers_json: '{\n}',
    use_provided_signature: false,
    force_bad_signature: false
  });
  const [testResult, setTestResult] = useState(null);
  const [testErr, setTestErr] = useState('');

  const [processLoading, setProcessLoading] = useState(false);
  const [processResult, setProcessResult] = useState(null);
  const [processErr, setProcessErr] = useState('');

  const loadFirms = async () => {
    const res = await api.get('/firms');
    setFirms(Array.isArray(res.data?.data) ? res.data.data : []);
  };

  const loadIntegrations = async () => {
    const res = await api.get('/integrations', {
      params: {
        firm_slug: filterFirm || undefined,
        provider: filterProvider || undefined
      }
    });
    setIntegrations(Array.isArray(res.data?.data) ? res.data.data : []);
  };

  const loadEvents = async (integrationId, filtersOverride, pagingOverride) => {
    setEventsLoading(true);
    try {
      const effectiveFilters = filtersOverride || eventsFilter;
      const effectiveLimit = pagingOverride?.limit ?? eventsLimit;
      const effectiveOffset = pagingOverride?.offset ?? eventsOffset;
      const res = await api.get(`/integrations/${integrationId}/events`, {
        params: {
          limit: effectiveLimit,
          offset: effectiveOffset,
          status: effectiveFilters.status || undefined,
          event_type: effectiveFilters.event_type || undefined,
          provider_event_id: effectiveFilters.provider_event_id || undefined,
          raw_body_sha256: effectiveFilters.raw_body_sha256 || undefined,
          payload_sha256: effectiveFilters.payload_sha256 || undefined
        }
      });
      setEvents(Array.isArray(res.data?.data) ? res.data.data : []);
      setEventsTotal(Number(res.data?.total || 0));
      setSelectedEvent(null);
      setEventNote('');
      setEventActions([]);
      setEventActionsErr('');
    } finally {
      setEventsLoading(false);
    }
  };

  const loadEventActions = async (integrationId, eventId) => {
    setEventActionsLoading(true);
    setEventActionsErr('');
    try {
      const res = await api.get(`/integrations/${integrationId}/events/${eventId}/actions`);
      setEventActions(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      setEventActions([]);
      setEventActionsErr(e?.error || e?.message || 'Actions yüklenemedi');
    } finally {
      setEventActionsLoading(false);
    }
  };

  useEffect(() => {
    document.title = 'FBAJANS - Entegrasyonlar';

    (async () => {
      try {
        setLoading(true);
        await Promise.all([loadFirms(), loadIntegrations()]);
      } catch (e) {
        setError(e?.message || 'Entegrasyonlar yüklenemedi');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (loading) return;
    (async () => {
      try {
        await loadIntegrations();
      } catch (e) {
        setError(e?.message || 'Entegrasyonlar yüklenemedi');
      }
    })();
  }, [filterFirm, filterProvider]);

  const providerLabel = useMemo(() => {
    const map = new Map(PROVIDERS.map(p => [p.value, p.label]));
    return (p) => map.get(p) || p;
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!createForm.firm_slug) {
      setError('Firma seçmelisiniz');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        firm_slug: createForm.firm_slug,
        provider: createForm.provider,
        status: createForm.status,
        settings: {
          ...(createForm.enqueue_to_message_pool ? { enqueue_to_message_pool: true } : {}),
          ...(createForm.default_country ? { default_country: String(createForm.default_country).toUpperCase() } : {})
        },
        secrets: createForm.webhook_secret
          ? { webhook_secret: createForm.webhook_secret }
          : {}
      };

      const res = await api.post('/integrations', payload);
      const created = res.data?.data;

      setSuccess('✅ Entegrasyon oluşturuldu');
      await loadIntegrations();

      if (created?.id) {
        setSelectedIntegration(created);
        await loadEvents(created.id);
      }
    } catch (e2) {
      setError(e2?.error || e2?.message || 'Entegrasyon oluşturulamadı');
    } finally {
      setSaving(false);
    }
  };

  const onSelectIntegration = async (row) => {
    setEditErr('');
    setEditOk('');

    let full = row;
    try {
      const res = await api.get(`/integrations/${row.id}`);
      full = res.data?.data || row;
    } catch {
      // keep row fallback
    }

    setSelectedIntegration(full);
    setEditForm({
      status: full?.status || 'active',
      enqueue_to_message_pool: Boolean(full?.settings?.enqueue_to_message_pool),
      default_country: String(full?.settings?.default_country || 'TR').toUpperCase(),
      webhook_secret: '' // blank => no change
    });

    setTestResult(null);
    setTestErr('');
    setSelectedEvent(null);
    setEventNote('');
    setProcessResult(null);
    setProcessErr('');
    setEventsOffset(0);
    await loadEvents(row.id, undefined, { offset: 0 });
  };

  const onApplyEventFilters = async () => {
    if (!selectedIntegration?.id) return;
    setEventsOffset(0);
    await loadEvents(selectedIntegration.id, undefined, { offset: 0 });
  };

  const onSaveIntegration = async () => {
    if (!selectedIntegration?.id || !editForm) return;
    setEditErr('');
    setEditOk('');
    setEditSaving(true);
    try {
      const settings = {
        ...(selectedIntegration?.settings || {}),
        enqueue_to_message_pool: Boolean(editForm.enqueue_to_message_pool),
        default_country: editForm.default_country ? String(editForm.default_country).toUpperCase() : 'TR'
      };

      const payload = {
        status: editForm.status,
        settings
      };

      if (editForm.webhook_secret && String(editForm.webhook_secret).trim()) {
        payload.secrets = {
          ...(selectedIntegration?.secrets || {}),
          webhook_secret: String(editForm.webhook_secret).trim()
        };
      }

      const res = await api.put(`/integrations/${selectedIntegration.id}`, payload);
      const updated = res.data?.data;
      if (updated) {
        setSelectedIntegration(updated);
        setEditForm(prev => ({
          ...prev,
          status: updated?.status || prev.status,
          enqueue_to_message_pool: Boolean(updated?.settings?.enqueue_to_message_pool),
          default_country: String(updated?.settings?.default_country || prev.default_country || 'TR').toUpperCase(),
          webhook_secret: ''
        }));
      }

      await loadIntegrations();
      setEditOk('✅ Kaydedildi');
      setTimeout(() => setEditOk(''), 1500);
    } catch (e) {
      setEditErr(e?.error || e?.message || 'Kaydedilemedi');
    } finally {
      setEditSaving(false);
    }
  };

  const onProcessEvents = async () => {
    setProcessErr('');
    setProcessResult(null);
    if (!selectedIntegration?.id) {
      setProcessErr('Önce bir entegrasyon seçin');
      return;
    }
    setProcessLoading(true);
    try {
      const res = await api.post(`/integrations/${selectedIntegration.id}/process`, { limit: 20 });
      setProcessResult(res.data);
      await loadEvents(selectedIntegration.id);
    } catch (e) {
      setProcessErr(e?.error || e?.message || 'Process başarısız');
    } finally {
      setProcessLoading(false);
    }
  };

  const onSelectEvent = async (ev) => {
    setSelectedEvent(ev);
    setEventNote(ev?.error_message || '');
    if (selectedIntegration?.id && ev?.id) {
      await loadEventActions(selectedIntegration.id, ev.id);
    }
  };

  const updateEventStatus = async (status) => {
    if (!selectedIntegration?.id || !selectedEvent?.id) return;
    setEventActionLoading(true);
    try {
      await api.post(`/integrations/${selectedIntegration.id}/events/${selectedEvent.id}/status`, {
        status,
        error_message: eventNote || undefined
      });
      await loadEvents(selectedIntegration.id);
      setSuccess('✅ Event güncellendi');
      setTimeout(() => setSuccess(''), 1500);
    } catch (e) {
      setError(e?.error || e?.message || 'Event güncellenemedi');
    } finally {
      setEventActionLoading(false);
    }
  };

  const prettyMaybeJson = (value) => {
    if (value == null) return '';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    const s = String(value);
    try {
      return JSON.stringify(JSON.parse(s), null, 2);
    } catch {
      return s;
    }
  };

  const onCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      setSuccess('✅ Kopyalandı');
      setTimeout(() => setSuccess(''), 1500);
    } catch {
      setError('Kopyalanamadı (tarayıcı izin vermedi)');
    }
  };

  const onSendTestWebhook = async () => {
    setTestErr('');
    setTestResult(null);
    if (!selectedIntegration?.id) {
      setTestErr('Önce bir entegrasyon seçin');
      return;
    }

    let payload;
    try {
      payload = JSON.parse(testForm.payload_json || '{}');
    } catch {
      setTestErr('Payload JSON geçersiz');
      return;
    }

    let headers;
    try {
      headers = JSON.parse(testForm.headers_json || '{}');
    } catch {
      setTestErr('Headers JSON geçersiz');
      return;
    }

    setTestSending(true);
    try {
      const res = await api.post(`/integrations/${selectedIntegration.id}/test-webhook`, {
        provider: selectedIntegration.provider,
        event_type: testForm.event_type || undefined,
        provider_event_id: testForm.provider_event_id || undefined,
        payload,
        headers,
        use_provided_signature: Boolean(testForm.use_provided_signature),
        force_bad_signature: Boolean(testForm.force_bad_signature)
      });
      setTestResult(res.data);
      await loadEvents(selectedIntegration.id);
    } catch (e) {
      setTestErr(e?.error || e?.message || 'Test webhook başarısız');
    } finally {
      setTestSending(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: 24 }}>Yükleniyor...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0 }}>Entegrasyonlar (Webhook)</h1>
          <div style={{ fontSize: 13, color: '#666' }}>
            Mevcut OpenCart/VaktiniBildir akışına dokunmaz.
          </div>
        </div>

        {error && (
          <div style={{ marginTop: 12, padding: 12, background: '#f8d7da', color: '#721c24', borderRadius: 8 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ marginTop: 12, padding: 12, background: '#d1e7dd', color: '#0f5132', borderRadius: 8 }}>
            {success}
          </div>
        )}

        {/* Create */}
        <div style={{ marginTop: 16, padding: 16, background: 'white', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
          <h2 style={{ marginTop: 0 }}>Yeni webhook entegrasyonu ekle</h2>
          <form onSubmit={onCreate} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Firma</label>
              <select
                value={createForm.firm_slug}
                onChange={(e) => setCreateForm(prev => ({ ...prev, firm_slug: e.target.value }))}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
              >
                <option value="">Seçiniz...</option>
                {firms.map(f => (
                  <option key={f.slug} value={f.slug}>{f.name} ({f.slug})</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Provider</label>
              <select
                value={createForm.provider}
                onChange={(e) => setCreateForm(prev => ({ ...prev, provider: e.target.value }))}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
              >
                {PROVIDERS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Durum</label>
              <select
                value={createForm.status}
                onChange={(e) => setCreateForm(prev => ({ ...prev, status: e.target.value }))}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
              >
                <option value="active">active</option>
                <option value="disabled">disabled</option>
              </select>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                Webhook Secret (Shopify için zorunlu, diğerleri için opsiyonel)
              </label>
              <input
                type="text"
                value={createForm.webhook_secret}
                onChange={(e) => setCreateForm(prev => ({ ...prev, webhook_secret: e.target.value }))}
                placeholder="örn: shopify webhook secret"
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Default Country (telefon normalize)</label>
              <select
                value={createForm.default_country}
                onChange={(e) => setCreateForm(prev => ({ ...prev, default_country: e.target.value }))}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
              >
                <option value="TR">TR</option>
                <option value="US">US</option>
                <option value="DE">DE</option>
                <option value="GB">GB</option>
                <option value="NL">NL</option>
              </select>
              <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                Enqueue açıkken telefon E.164'e çevrilirken kullanılır.
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={!!createForm.enqueue_to_message_pool}
                  onChange={(e) => setCreateForm(prev => ({ ...prev, enqueue_to_message_pool: e.target.checked }))}
                />
                Draft mesajları Message Pool'a kuyruğa ekle (opt-in; `ENABLE_MESSAGE_POOL=1` gerekir)
              </label>
              <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                Not: Bu seçim mesajı hemen göndermez; sadece havuza enqueue eder. Gönderim için MessagePool worker ve ayarları ayrıca açılmalıdır.
              </div>
            </div>

            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="submit"
                disabled={saving}
                style={{
                  padding: '10px 14px',
                  border: 'none',
                  borderRadius: 10,
                  background: saving ? '#999' : '#0d6efd',
                  color: 'white',
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                {saving ? 'Kaydediliyor...' : 'Entegrasyonu Oluştur'}
              </button>
            </div>
          </form>
        </div>

        {/* Filters + table */}
        <div style={{ marginTop: 16, padding: 16, background: 'white', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <h2 style={{ margin: 0 }}>Mevcut entegrasyonlar</h2>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <select
                value={filterFirm}
                onChange={(e) => setFilterFirm(e.target.value)}
                style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
              >
                <option value="">Tüm firmalar</option>
                {firms.map(f => (
                  <option key={f.slug} value={f.slug}>{f.name} ({f.slug})</option>
                ))}
              </select>

              <select
                value={filterProvider}
                onChange={(e) => setFilterProvider(e.target.value)}
                style={{ padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
              >
                <option value="">Tüm provider'lar</option>
                {PROVIDERS.map(p => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ overflowX: 'auto', marginTop: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['ID', 'Firma', 'Provider', 'Durum', 'Webhook URL'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #eee', fontSize: 13, color: '#444' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {integrations.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: 12, color: '#666' }}>Kayıt yok</td>
                  </tr>
                ) : integrations.map(row => (
                  <tr
                    key={row.id}
                    onClick={() => onSelectIntegration(row)}
                    style={{ cursor: 'pointer', background: selectedIntegration?.id === row.id ? '#f2f7ff' : 'transparent' }}
                  >
                    <td style={{ padding: 10, borderBottom: '1px solid #f2f2f2' }}>{row.id}</td>
                    <td style={{ padding: 10, borderBottom: '1px solid #f2f2f2' }}>{row.firm_slug}</td>
                    <td style={{ padding: 10, borderBottom: '1px solid #f2f2f2' }}>{providerLabel(row.provider)}</td>
                    <td style={{ padding: 10, borderBottom: '1px solid #f2f2f2' }}>{row.status}</td>
                    <td style={{ padding: 10, borderBottom: '1px solid #f2f2f2', fontSize: 12, color: '#0d6efd' }}>
                      {buildWebhookUrl(row.id, row.provider)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Events */}
        <div style={{ marginTop: 16, padding: 16, background: 'white', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
          <h2 style={{ marginTop: 0 }}>Webhook event log</h2>
          {!selectedIntegration ? (
            <div style={{ color: '#666' }}>Bir entegrasyon seçin.</div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: '#444', marginBottom: 10 }}>
                Seçili: #{selectedIntegration.id} / {selectedIntegration.firm_slug} / {providerLabel(selectedIntegration.provider)}
                {' '}— toplam event: {eventsTotal}
              </div>

              <div style={{
                display: 'flex',
                gap: 10,
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 12,
                padding: 12,
                background: '#f8f9fa',
                borderRadius: 10,
                border: '1px solid #eee'
              }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div style={{ fontSize: 12, color: '#555' }}>Sayfa boyutu</div>
                  <select
                    value={eventsLimit}
                    onChange={(e) => {
                      const nextLimit = Number(e.target.value) || 50;
                      setEventsLimit(nextLimit);
                      setEventsOffset(0);
                      if (selectedIntegration?.id) {
                        loadEvents(selectedIntegration.id, undefined, { limit: nextLimit, offset: 0 });
                      }
                    }}
                    style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}
                  >
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    type="button"
                    disabled={eventsOffset <= 0 || eventsLoading}
                    onClick={() => {
                      const nextOffset = Math.max(0, (eventsOffset || 0) - (eventsLimit || 50));
                      setEventsOffset(nextOffset);
                      loadEvents(selectedIntegration.id, undefined, { offset: nextOffset });
                    }}
                    style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}
                  >
                    Önceki
                  </button>
                  <button
                    type="button"
                    disabled={eventsLoading || (eventsOffset + eventsLimit) >= eventsTotal}
                    onClick={() => {
                      const nextOffset = (eventsOffset || 0) + (eventsLimit || 50);
                      setEventsOffset(nextOffset);
                      loadEvents(selectedIntegration.id, undefined, { offset: nextOffset });
                    }}
                    style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}
                  >
                    Sonraki
                  </button>
                  <div style={{ fontSize: 12, color: '#555' }}>
                    Sayfa {Math.floor((eventsOffset || 0) / (eventsLimit || 50)) + 1}
                    / {Math.max(1, Math.ceil((eventsTotal || 0) / (eventsLimit || 50)))}
                  </div>
                </div>
              </div>

              {/* Integration settings */}
              {editForm && (
                <div style={{
                  marginBottom: 14,
                  padding: 14,
                  borderRadius: 12,
                  border: '1px solid #eee'
                }}>
                  <h3 style={{ marginTop: 0 }}>Entegrasyon ayarları</h3>

                  {editErr && (
                    <div style={{ marginBottom: 10, padding: 10, background: '#f8d7da', color: '#721c24', borderRadius: 8 }}>
                      {editErr}
                    </div>
                  )}
                  {editOk && (
                    <div style={{ marginBottom: 10, padding: 10, background: '#d1e7dd', color: '#0f5132', borderRadius: 8 }}>
                      {editOk}
                    </div>
                  )}

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Durum</label>
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
                        style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                      >
                        <option value="active">active</option>
                        <option value="disabled">disabled</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Default Country</label>
                      <select
                        value={editForm.default_country}
                        onChange={(e) => setEditForm(prev => ({ ...prev, default_country: e.target.value }))}
                        style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                      >
                        <option value="TR">TR</option>
                        <option value="US">US</option>
                        <option value="DE">DE</option>
                        <option value="GB">GB</option>
                        <option value="NL">NL</option>
                      </select>
                    </div>

                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                        <input
                          type="checkbox"
                          checked={!!editForm.enqueue_to_message_pool}
                          onChange={(e) => setEditForm(prev => ({ ...prev, enqueue_to_message_pool: e.target.checked }))}
                        />
                        Draft mesajları Message Pool'a kuyruğa ekle (opt-in)
                      </label>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                        Etkinleşmesi için backend tarafında `ENABLE_MESSAGE_POOL=1` gerekir.
                      </div>
                    </div>

                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Webhook Secret (boş bırakılırsa değişmez)</label>
                      <input
                        type="text"
                        value={editForm.webhook_secret}
                        onChange={(e) => setEditForm(prev => ({ ...prev, webhook_secret: e.target.value }))}
                        placeholder="örn: shopify webhook secret"
                        style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                    <button
                      type="button"
                      disabled={editSaving}
                      onClick={onSaveIntegration}
                      style={{
                        padding: '10px 14px',
                        border: 'none',
                        borderRadius: 10,
                        background: editSaving ? '#999' : '#0d6efd',
                        color: 'white',
                        cursor: editSaving ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {editSaving ? 'Kaydediliyor...' : 'Ayarları Kaydet'}
                    </button>
                  </div>
                </div>
              )}

              <div style={{
                display: 'flex',
                gap: 10,
                flexWrap: 'wrap',
                alignItems: 'center',
                marginBottom: 12,
                padding: 12,
                background: '#f8f9fa',
                borderRadius: 10,
                border: '1px solid #eee'
              }}>
                <div style={{ flex: 1, minWidth: 260, fontSize: 12, color: '#0d6efd' }}>
                  {buildWebhookUrl(selectedIntegration.id, selectedIntegration.provider)}
                </div>
                <button
                  type="button"
                  onClick={() => onCopy(buildWebhookUrl(selectedIntegration.id, selectedIntegration.provider))}
                  style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}
                >
                  Kopyala
                </button>
              </div>

              <div style={{
                marginBottom: 14,
                padding: 14,
                borderRadius: 12,
                border: '1px solid #eee'
              }}>
                <h3 style={{ marginTop: 0 }}>Event filtre (hash)</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>status</label>
                    <select
                      value={eventsFilter.status}
                      onChange={(e) => setEventsFilter(prev => ({ ...prev, status: e.target.value }))}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', fontSize: 12 }}
                    >
                      <option value="">(hepsi)</option>
                      <option value="received">received</option>
                      <option value="processing">processing</option>
                      <option value="retry">retry</option>
                      <option value="processed">processed</option>
                      <option value="ignored">ignored</option>
                      <option value="failed">failed</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>event_type</label>
                    <input
                      type="text"
                      value={eventsFilter.event_type}
                      onChange={(e) => setEventsFilter(prev => ({ ...prev, event_type: e.target.value }))}
                      placeholder="orders/create"
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>provider_event_id</label>
                    <input
                      type="text"
                      value={eventsFilter.provider_event_id}
                      onChange={(e) => setEventsFilter(prev => ({ ...prev, provider_event_id: e.target.value }))}
                      placeholder="..."
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>raw_body_sha256</label>
                    <input
                      type="text"
                      value={eventsFilter.raw_body_sha256}
                      onChange={(e) => setEventsFilter(prev => ({ ...prev, raw_body_sha256: e.target.value }))}
                      placeholder="64 hex"
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>payload_sha256</label>
                    <input
                      type="text"
                      value={eventsFilter.payload_sha256}
                      onChange={(e) => setEventsFilter(prev => ({ ...prev, payload_sha256: e.target.value }))}
                      placeholder="64 hex"
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
                  <button
                    type="button"
                    onClick={() => {
                      const cleared = { status: '', event_type: '', provider_event_id: '', raw_body_sha256: '', payload_sha256: '' };
                      setEventsFilter(cleared);
                      if (selectedIntegration?.id) {
                        setEventsOffset(0);
                        loadEvents(selectedIntegration.id, cleared, { offset: 0 });
                      }
                    }}
                    style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}
                  >
                    Temizle
                  </button>
                  <button
                    type="button"
                    onClick={onApplyEventFilters}
                    style={{ padding: '10px 14px', border: 'none', borderRadius: 10, background: '#0d6efd', color: 'white', cursor: 'pointer' }}
                  >
                    Filtrele
                  </button>
                </div>
              </div>

              <div style={{
                marginBottom: 14,
                padding: 14,
                borderRadius: 12,
                border: '1px solid #eee'
              }}>
                <h3 style={{ marginTop: 0 }}>Test webhook</h3>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
                  Bu test sadece event ingest eder (WhatsApp mesajı göndermez). Shopify için HMAC otomatik üretilir.
                </div>

                {testErr && (
                  <div style={{ marginBottom: 10, padding: 10, background: '#f8d7da', color: '#721c24', borderRadius: 8 }}>
                    {testErr}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                      Event Type / Topic
                    </label>
                    <input
                      type="text"
                      value={testForm.event_type}
                      onChange={(e) => setTestForm(prev => ({ ...prev, event_type: e.target.value }))}
                      placeholder={selectedIntegration.provider === 'shopify' ? 'orders/create' : `${selectedIntegration.provider}.test`}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                      Provider Event ID (opsiyonel)
                    </label>
                    <input
                      type="text"
                      value={testForm.provider_event_id}
                      onChange={(e) => setTestForm(prev => ({ ...prev, provider_event_id: e.target.value }))}
                      placeholder="test_..."
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd' }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                      Payload (JSON)
                    </label>
                    <textarea
                      value={testForm.payload_json}
                      onChange={(e) => setTestForm(prev => ({ ...prev, payload_json: e.target.value }))}
                      rows={8}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', fontFamily: 'monospace', fontSize: 12 }}
                    />
                  </div>

                  <div style={{ gridColumn: '1 / -1' }}>
                    <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
                      Extra headers (JSON) (opsiyonel)
                    </label>
                    <textarea
                      value={testForm.headers_json}
                      onChange={(e) => setTestForm(prev => ({ ...prev, headers_json: e.target.value }))}
                      rows={6}
                      style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', fontFamily: 'monospace', fontSize: 12 }}
                    />
                    <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                      Shopify için örnek: {'{'}"x-shopify-hmac-sha256":"..."{'}'} (use provided signature açıkken)
                    </div>
                  </div>

                  <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={Boolean(testForm.use_provided_signature)}
                        onChange={(e) => setTestForm(prev => ({ ...prev, use_provided_signature: e.target.checked }))}
                      />
                      Shopify: provided signature kullan
                    </label>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                      <input
                        type="checkbox"
                        checked={Boolean(testForm.force_bad_signature)}
                        onChange={(e) => setTestForm(prev => ({ ...prev, force_bad_signature: e.target.checked }))}
                        disabled={Boolean(testForm.use_provided_signature)}
                      />
                      Shopify: bilerek hatalı imza üret
                    </label>
                  </div>

                  <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      disabled={testSending}
                      onClick={onSendTestWebhook}
                      style={{
                        padding: '10px 14px',
                        border: 'none',
                        borderRadius: 10,
                        background: testSending ? '#999' : '#198754',
                        color: 'white',
                        cursor: testSending ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {testSending ? 'Gönderiliyor...' : 'Test Webhook Gönder'}
                    </button>
                  </div>
                </div>

                {testResult && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                      Yanıt
                    </div>
                    <pre style={{
                      margin: 0,
                      padding: 12,
                      background: '#0b1020',
                      color: '#e6edf3',
                      borderRadius: 10,
                      overflowX: 'auto',
                      fontSize: 12
                    }}>
                      {JSON.stringify(testResult, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              <div style={{
                marginBottom: 14,
                padding: 14,
                borderRadius: 12,
                border: '1px solid #eee'
              }}>
                <h3 style={{ marginTop: 0 }}>Event işleme (manual)</h3>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 10 }}>
                  Bu buton “due” event’leri işler. Şimdilik handler’lar güvenli no-op (pipeline test). İleride gerçek aksiyonlar buraya bağlanacak.
                </div>

                {processErr && (
                  <div style={{ marginBottom: 10, padding: 10, background: '#f8d7da', color: '#721c24', borderRadius: 8 }}>
                    {processErr}
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    disabled={processLoading}
                    onClick={onProcessEvents}
                    style={{
                      padding: '10px 14px',
                      border: 'none',
                      borderRadius: 10,
                      background: processLoading ? '#999' : '#0d6efd',
                      color: 'white',
                      cursor: processLoading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {processLoading ? 'Çalışıyor...' : 'Eventleri İşle'}
                  </button>
                </div>

                {processResult && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                      Process sonucu
                    </div>
                    <pre style={{
                      margin: 0,
                      padding: 12,
                      background: '#0b1020',
                      color: '#e6edf3',
                      borderRadius: 10,
                      overflowX: 'auto',
                      fontSize: 12
                    }}>
                      {JSON.stringify(processResult, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              {eventsLoading ? (
                <div style={{ color: '#666' }}>Event'ler yükleniyor...</div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        {['ID', 'Event', 'Provider Event ID', 'Status', 'Attempts', 'Next Attempt', 'Received At'].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: 10, borderBottom: '1px solid #eee', fontSize: 13, color: '#444' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {events.length === 0 ? (
                        <tr><td colSpan={7} style={{ padding: 12, color: '#666' }}>Henüz event yok</td></tr>
                      ) : events.map(ev => (
                        <tr
                          key={ev.id}
                          onClick={() => onSelectEvent(ev)}
                          style={{ cursor: 'pointer', background: selectedEvent?.id === ev.id ? '#f2f7ff' : 'transparent' }}
                        >
                          <td style={{ padding: 10, borderBottom: '1px solid #f2f2f2' }}>{ev.id}</td>
                          <td style={{ padding: 10, borderBottom: '1px solid #f2f2f2' }}>{ev.event_type}</td>
                          <td style={{ padding: 10, borderBottom: '1px solid #f2f2f2', fontSize: 12 }}>{ev.provider_event_id}</td>
                          <td style={{ padding: 10, borderBottom: '1px solid #f2f2f2' }}>{ev.status}</td>
                          <td style={{ padding: 10, borderBottom: '1px solid #f2f2f2', fontSize: 12 }}>{ev.attempt_count ?? ''}</td>
                          <td style={{ padding: 10, borderBottom: '1px solid #f2f2f2', fontSize: 12 }}>{ev.next_attempt_at ?? ''}</td>
                          <td style={{ padding: 10, borderBottom: '1px solid #f2f2f2', fontSize: 12 }}>{ev.received_at}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedEvent && (
                <div style={{ marginTop: 12, padding: 14, borderRadius: 12, border: '1px solid #eee', background: '#fafafa' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                      Event detay: #{selectedEvent.id} ({selectedEvent.status})
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedEvent(null)}
                      style={{ padding: '6px 10px', borderRadius: 10, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}
                    >
                      Kapat
                    </button>
                  </div>

                  <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>Not / error_message</div>
                      <textarea
                        value={eventNote}
                        onChange={(e) => setEventNote(e.target.value)}
                        rows={3}
                        style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid #ddd', fontFamily: 'monospace', fontSize: 12 }}
                        placeholder="Opsiyonel: hata notu veya açıklama"
                      />
                    </div>

                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        disabled={eventActionLoading}
                        onClick={() => updateEventStatus('processed')}
                        style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #198754', background: '#198754', color: 'white', cursor: 'pointer' }}
                      >
                        Processed işaretle
                      </button>
                      <button
                        type="button"
                        disabled={eventActionLoading}
                        onClick={() => updateEventStatus('ignored')}
                        style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #6c757d', background: '#6c757d', color: 'white', cursor: 'pointer' }}
                      >
                        Ignored işaretle
                      </button>
                      <button
                        type="button"
                        disabled={eventActionLoading}
                        onClick={() => updateEventStatus('failed')}
                        style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #dc3545', background: '#dc3545', color: 'white', cursor: 'pointer' }}
                      >
                        Failed işaretle
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, marginTop: 12 }}>
                    <div>
                      <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>Actions (iş çıktıları)</div>
                      <pre style={{ margin: 0, padding: 12, background: '#0b1020', color: '#e6edf3', borderRadius: 10, overflowX: 'auto', fontSize: 12 }}>
                        {eventActionsLoading
                          ? 'Yükleniyor...'
                          : eventActionsErr
                            ? `Hata: ${eventActionsErr}`
                            : prettyMaybeJson(eventActions)}
                      </pre>
                    </div>

                    <div>
                      <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>Hashes</div>
                      <pre style={{ margin: 0, padding: 12, background: '#0b1020', color: '#e6edf3', borderRadius: 10, overflowX: 'auto', fontSize: 12 }}>
                        {prettyMaybeJson({
                          raw_body_sha256: selectedEvent.raw_body_sha256 || null,
                          payload_sha256: selectedEvent.payload_sha256 || null
                        })}
                      </pre>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                        <button
                          type="button"
                          disabled={!selectedEvent.raw_body_sha256}
                          onClick={() => onCopy(selectedEvent.raw_body_sha256)}
                          style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}
                        >
                          raw_body_sha256 kopyala
                        </button>
                        <button
                          type="button"
                          disabled={!selectedEvent.raw_body_sha256 || !selectedIntegration?.id}
                          onClick={() => {
                            const next = { ...eventsFilter, raw_body_sha256: selectedEvent.raw_body_sha256 || '' };
                            setEventsFilter(next);
                            setEventsOffset(0);
                            loadEvents(selectedIntegration.id, next, { offset: 0 });
                          }}
                          style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #0d6efd', background: '#0d6efd', color: 'white', cursor: 'pointer' }}
                        >
                          raw ile filtrele
                        </button>

                        <button
                          type="button"
                          disabled={!selectedEvent.payload_sha256}
                          onClick={() => onCopy(selectedEvent.payload_sha256)}
                          style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}
                        >
                          payload_sha256 kopyala
                        </button>
                        <button
                          type="button"
                          disabled={!selectedEvent.payload_sha256 || !selectedIntegration?.id}
                          onClick={() => {
                            const next = { ...eventsFilter, payload_sha256: selectedEvent.payload_sha256 || '' };
                            setEventsFilter(next);
                            setEventsOffset(0);
                            loadEvents(selectedIntegration.id, next, { offset: 0 });
                          }}
                          style={{ padding: '8px 10px', borderRadius: 10, border: '1px solid #0d6efd', background: '#0d6efd', color: 'white', cursor: 'pointer' }}
                        >
                          payload ile filtrele
                        </button>
                      </div>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                        raw_body_sha256: imza doğrulamada kullanılan ham body.
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>Headers</div>
                      <pre style={{ margin: 0, padding: 12, background: '#0b1020', color: '#e6edf3', borderRadius: 10, overflowX: 'auto', fontSize: 12 }}>
                        {prettyMaybeJson(selectedEvent.headers_json)}
                      </pre>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: '#555', marginBottom: 6 }}>Payload</div>
                      <pre style={{ margin: 0, padding: 12, background: '#0b1020', color: '#e6edf3', borderRadius: 10, overflowX: 'auto', fontSize: 12 }}>
                        {prettyMaybeJson(selectedEvent.payload_json)}
                      </pre>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
